// ============================================================================
// KLINISOL CRO - SOP Library Page (REDESIGNED)
// Pure Tailwind CSS - Matches Dashboard/Navbar design system
// Protected - Displays Standard Operating Procedures
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import SOPViewer from '../components/SOPViewer';
import { 
    Upload, 
    Search, 
    Filter, 
    Eye, 
    Download, 
    FileText, 
    LayoutGrid,
    PowerOff,
    Home,
    User,
    Shield,
    Calendar,
    Tag,
    AlertCircle,
    RotateCcw,
    Loader2,
    X,
    ClipboardList
} from 'lucide-react';

const SOPLibrary = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // State management
    const [sops, setSops] = useState([]);
    const [filteredSops, setFilteredSops] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');

    // Viewer state
    const [viewerOpen, setViewerOpen] = useState(false);
    const [selectedSop, setSelectedSop] = useState(null);

    // Fetch SOPs on component mount
    useEffect(() => {
        fetchSOPs();
        fetchCategories();
    }, []);

    // Apply filters when search term or category changes
    useEffect(() => {
        applyFilters();
    }, [searchTerm, selectedCategory, sops]);

    // Listen for openSOP events from enhanced viewer (for clickable Master Register links)
    useEffect(() => {
        const handleOpenSOP = (event) => {
            const { id, sop_number, file_type, fromMasterRegister, masterRegisterId } = event.detail;
            const sopToOpen = { 
                id, 
                sop_number, 
                file_type,
                fromMasterRegister: fromMasterRegister || false,
                masterRegisterId
            };
            handleViewSOP(sopToOpen);
        };

        window.addEventListener('openSOP', handleOpenSOP);
        
        return () => {
            window.removeEventListener('openSOP', handleOpenSOP);
        };
    }, []);

    const fetchSOPs = async () => {
        try {
            setLoading(true);
            setError('');
            
            const response = await api.get('/sops');
            
            if (response.data.success) {
                setSops(response.data.data);
                setFilteredSops(response.data.data);
            } else {
                setError('Failed to load SOPs');
            }
        } catch (err) {
            console.error('Error fetching SOPs:', err);
            setError(err.response?.data?.message || 'Failed to load SOPs. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await api.get('/sops/categories');
            if (response.data.success) {
                setCategories(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    };

    const applyFilters = () => {
        let filtered = [...sops];

        // Apply category filter
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(sop => sop.category === selectedCategory);
        }

        // Apply search filter
        if (searchTerm.trim()) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(sop =>
                sop.sop_number.toLowerCase().includes(search) ||
                sop.title.toLowerCase().includes(search) ||
                (sop.description && sop.description.toLowerCase().includes(search))
            );
        }

        setFilteredSops(filtered);
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const handleViewSOP = (sop) => {
        setSelectedSop(sop);
        setViewerOpen(true);
    };

    const handleDownloadSOP = async (sopId, sopNumber, fileType) => {
        try {
            const response = await api.get(`/sops/${sopId}/download`, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data]);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${sopNumber}.${fileType}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

        } catch (err) {
            console.error('Error downloading SOP:', err);
            alert('Failed to download SOP. Please try again.');
        }
    };

    const getCategoryColor = (category) => {
        const colors = {
            'Quality Management': 'bg-blue-100 text-blue-800 border-blue-300',
            'Clinical Operations': 'bg-cyan-100 text-cyan-800 border-cyan-300',
            'Regulatory Affairs': 'bg-purple-100 text-purple-800 border-purple-300',
            'Data Management': 'bg-green-100 text-green-800 border-green-300',
            'Safety': 'bg-red-100 text-red-800 border-red-300',
            'default': 'bg-gray-100 text-gray-800 border-gray-300'
        };
        return colors[category] || colors.default;
    };

    return (
        <div className="min-h-screen bg-[#f8fafd]">
            {/* Unified Header */}
            <header className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white shadow-lg sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                        {/* Title Section */}
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold">
                                KLIN<span className="text-[#E91E63]">i</span>SOL Document Control System
                            </h1>
                            <p className="text-sm opacity-90 mt-1">Designed for FDA 21 CFR Part 11 Compliance</p>
                        </div>
                        
                        {/* 4 Mini-Card Buttons */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => navigate('/sop-library')}
                                className="px-4 py-2 bg-white text-[#475569] rounded-lg font-semibold flex items-center gap-2 shadow-md"
                            >
                                <FileText className="w-5 h-5" />
                                <span className="hidden sm:inline">SOPs</span>
                            </button>
                            <button
                                onClick={() => navigate('/forms-library')}
                                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold flex items-center gap-2 transition-all"
                            >
                                <ClipboardList className="w-5 h-5" />
                                <span className="hidden sm:inline">Forms</span>
                            </button>
                            {user?.id === 1 && (
                                <button 
                                    onClick={() => navigate('/sop-upload')} 
                                    className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold flex items-center gap-2 transition-all"
                                >
                                    <Upload className="w-5 h-5" />
                                    <span className="hidden sm:inline">Upload</span>
                                </button>
                            )}
                            <button 
                                onClick={() => navigate('/dashboard')} 
                                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold flex items-center gap-2 transition-all"
                            >
                                <Home className="w-5 h-5" />
                                <span className="hidden sm:inline">Dashboard</span>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Page Title */}
                <div className="mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">SOP Library</h2>
                    <p className="text-gray-600">Access KLIN<span className="text-[#E91E63]">i</span>SOL's comprehensive library of Standard Operating Procedures</p>
                </div>

                {/* Filters Section */}
                <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 mb-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                        {/* Search Box */}
                        <div className="lg:col-span-2">
                            <label className="block text-sm font-semibold text-gray-900 mb-2">Search SOPs</label>
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by SOP number, title, or description..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-12 pr-10 py-3 border-2 border-gray-200 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:border-[#475569] focus:bg-white focus:ring-4 focus:ring-[#475569]/10"
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Category Filter */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">Filter by Category</label>
                            <div className="relative">
                                <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <select
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:border-[#475569] focus:bg-white focus:ring-4 focus:ring-[#475569]/10 appearance-none cursor-pointer"
                                >
                                    <option value="all">All Categories</option>
                                    {categories.map((cat) => (
                                        <option key={cat.category} value={cat.category}>
                                            {cat.category} ({cat.count})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Results Count */}
                    <div className="flex items-center justify-between text-sm">
                        <div className="text-gray-600 font-medium">
                            Showing <span className="text-[#475569] font-bold">{filteredSops.length}</span> of <span className="font-bold">{sops.length}</span> SOPs
                        </div>
                        {(searchTerm || selectedCategory !== 'all') && (
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    setSelectedCategory('all');
                                }}
                                className="text-[#475569] hover:text-[#334155] font-semibold flex items-center gap-1"
                            >
                                <X className="w-4 h-4" />
                                Clear Filters
                            </button>
                        )}
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="flex flex-col items-center justify-center py-20">
                        <Loader2 className="w-12 h-12 text-[#475569] animate-spin mb-4" />
                        <p className="text-lg text-gray-600 font-semibold">Loading SOPs...</p>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-500 rounded-2xl p-8 text-center">
                        <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
                        <p className="text-lg text-red-900 mb-6">{error}</p>
                        <button 
                            onClick={fetchSOPs} 
                            className="px-6 py-3 bg-gradient-to-r from-[#475569] to-[#334155] text-white rounded-xl font-bold hover:-translate-y-0.5 transition-all shadow-lg flex items-center gap-2 mx-auto"
                        >
                            <RotateCcw className="w-5 h-5" />
                            <span>Retry</span>
                        </button>
                    </div>
                )}

                {/* SOPs Grid */}
                {!loading && !error && filteredSops.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredSops.map((sop) => (
                            <div 
                                key={sop.id} 
                                className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md hover:border-[#E91E63]/50 transition-all overflow-hidden flex flex-col"
                            >
                                {/* Card Header */}
                                <div className="p-6 pb-4 border-b border-gray-100">
                                    <div className="flex items-start justify-between gap-2 mb-3">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${getCategoryColor(sop.category)}`}>
                                            {sop.category}
                                        </span>
                                        <span className="px-3 py-1 bg-[#475569] text-white rounded-full text-xs font-bold">
                                            {sop.sop_number}
                                        </span>
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900 line-clamp-2">
                                        {sop.title}
                                    </h3>
                                </div>

                                {/* Card Body */}
                                <div className="p-6 pt-4 flex-1">
                                    {sop.description && (
                                        <p className="text-sm text-gray-600 line-clamp-3 mb-4">
                                            {sop.description}
                                        </p>
                                    )}
                                    {sop.is_template && (
                                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-green-50 to-green-100 border border-green-300 text-green-800 rounded-lg text-xs font-semibold mb-4">
                                            <FileText className="w-3 h-3" />
                                            Fillable Template Available
                                        </div>
                                    )}
                                    
                                    {/* Metadata */}
                                    <div className="space-y-2 text-xs">
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <Tag className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="font-semibold">Version:</span>
                                            <span>{sop.version}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="font-semibold">Effective:</span>
                                            <span>{new Date(sop.effective_date + 'T00:00:00Z').toLocaleDateString('en-US', {timeZone: 'UTC'})}</span>
                                        </div>
                                        {sop.owner && (
                                            <div className="flex items-center gap-2 text-gray-600">
                                                <User className="w-3.5 h-3.5 text-gray-400" />
                                                <span className="font-semibold">Owner:</span>
                                                <span className="truncate">{sop.owner}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Card Footer */}
                                <div className="p-4 border-t border-gray-100 bg-gray-50 flex gap-2">
                                    <button 
                                        onClick={() => handleViewSOP(sop)}
                                        className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#475569] to-[#334155] text-white rounded-lg font-semibold hover:-translate-y-0.5 transition-all shadow-sm flex items-center justify-center gap-2"
                                    >
                                        <Eye className="w-4 h-4" />
                                        <span>View</span>
                                    </button>
                                    <button 
                                        onClick={() => handleDownloadSOP(sop.id, sop.sop_number, sop.file_type)}
                                        className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#E91E63] to-[#C2185B] text-white rounded-lg font-semibold hover:-translate-y-0.5 transition-all shadow-sm flex items-center justify-center gap-2"
                                    >
                                        <Download className="w-4 h-4" />
                                        <span>Download</span>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* No Results */}
                {!loading && !error && filteredSops.length === 0 && (
                    <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-12 text-center">
                        <FileText className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">No SOPs Found</h3>
                        <p className="text-gray-600 mb-6">
                            {searchTerm || selectedCategory !== 'all'
                                ? 'Try adjusting your filters or search term.'
                                : 'No SOPs are currently available.'}
                        </p>
                        {(searchTerm || selectedCategory !== 'all') && (
                            <button 
                                onClick={() => {
                                    setSearchTerm('');
                                    setSelectedCategory('all');
                                }}
                                className="px-6 py-3 bg-gradient-to-r from-[#475569] to-[#334155] text-white rounded-xl font-bold hover:-translate-y-0.5 transition-all shadow-lg"
                            >
                                Clear Filters
                            </button>
                        )}
                    </div>
                )}
            </main>

            {/* Footer */}
            <footer className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-6 px-6 mt-12">
                <div className="max-w-7xl mx-auto text-center">
                    <h3 className="text-xl font-bold mb-2">
                        KLINiSOL
                    </h3>
                    <p className="text-sm opacity-90">&copy; 2025 KLINiSOL CRO. All rights reserved.</p>
                </div>
            </footer>

            {/* SOP Viewer Modal */}
            {viewerOpen && selectedSop && (
                <SOPViewer
                    sopId={selectedSop.id}
                    sopNumber={selectedSop.sop_number}
                    fileType={selectedSop.file_type}
                    fromMasterRegister={selectedSop.fromMasterRegister || false}
                    onClose={() => {
                        setViewerOpen(false);
                        setSelectedSop(null);
                    }}
                />
            )}
        </div>
    );
};

export default SOPLibrary;